import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, timeAgo } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Plus, Phone, Trash2, Star, Search, RotateCcw, MessageSquare } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";

export default function VendorsPage() {
  const { user } = useAuth();
  const [active, setActive] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [filter, setFilter] = useState("");
  const isCommittee = user?.role === "committee";

  const load = useCallback(async () => {
    const a = await api.get("/vendors", { params: { status: "active" } });
    setActive(a.data);
    if (isCommittee) {
      const i = await api.get("/vendors", { params: { status: "inactive" } });
      setInactive(i.data);
    }
  }, [isCommittee]);
  useEffect(() => { load(); }, [load]);

  async function deactivate(v) {
    if (!confirm(`Deactivate ${v.name}? Reviews are preserved — you can restore later.`)) return;
    try { await api.post(`/vendors/${v.id}/deactivate`); toast.success(`${v.name} deactivated`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  async function restore(v) {
    try { await api.post(`/vendors/${v.id}/restore`); toast.success(`${v.name} restored`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  const list = showInactive ? inactive : active;
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((v) =>
      v.name.toLowerCase().includes(q) ||
      v.service_type.toLowerCase().includes(q) ||
      (v.notes || "").toLowerCase().includes(q) ||
      (v.phone || "").includes(q)
    );
  }, [list, filter]);

  const groupedEntries = useMemo(() => {
    const acc = {};
    for (const v of filtered) (acc[v.service_type] ||= []).push(v);
    return Object.entries(acc).sort();
  }, [filtered]);

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Vendors</div>
          <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">Trusted contacts</h1>
          <p className="text-stone-600 text-sm mt-1">Plumbers, electricians, painters and other regulars. Any resident can add and rate. Committee deactivates poor performers (history is preserved).</p>
        </div>
        <AddVendorDialog onDone={load} />
      </div>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="vendor-filter"
            placeholder="Search by name or category (e.g. plumber)"
            className="h-10 pl-9" />
        </div>
        {isCommittee && (
          <div className="inline-flex border border-stone-200 rounded-full bg-white text-xs overflow-hidden" data-testid="vendor-toggle">
            <button onClick={() => setShowInactive(false)}
              data-testid="show-active"
              className={`px-3 py-1.5 ${!showInactive ? "bg-[#C05640] text-white" : "text-stone-600"}`}>
              Active ({active.length})
            </button>
            <button onClick={() => setShowInactive(true)}
              data-testid="show-inactive"
              className={`px-3 py-1.5 ${showInactive ? "bg-stone-700 text-white" : "text-stone-600"}`}>
              Inactive ({inactive.length})
            </button>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-7">
        {groupedEntries.map(([service, items]) => (
          <section key={service}>
            <h2 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{service}</h2>
            <div className="space-y-2">
              {items.map((v) => (
                <div key={v.id} className={`bg-white border rounded-xl p-4 ${v.status === "inactive" ? "border-stone-200 opacity-70" : "border-stone-200"}`} data-testid={`vendor-${v.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {v.name}
                        {v.status === "inactive" && <span className="text-[10px] uppercase tracking-wider font-bold bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">Inactive</span>}
                      </div>
                      {v.notes && <div className="text-xs text-stone-600 mt-0.5">{v.notes}</div>}
                      <ReviewSummary vendor={v} />
                      <div className="text-[11px] text-stone-400 mt-1">Added by {v.added_by_name}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {v.status !== "inactive" && <RateVendorDialog vendor={v} onDone={load} />}
                      {v.phone && (
                        <>
                          <a href={`tel:${v.phone}`} className="text-stone-500 hover:text-[#C05640] p-2" data-testid={`vendor-call-${v.id}`}><Phone size={16} /></a>
                          <a href={`https://wa.me/${v.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                            className="text-[#25D366] hover:opacity-80 p-2" data-testid={`vendor-wa-${v.id}`}><FaWhatsapp size={18} /></a>
                        </>
                      )}
                      {isCommittee && v.status !== "inactive" && (
                        <button onClick={() => deactivate(v)} className="text-stone-400 hover:text-red-600 p-2" data-testid={`vendor-deactivate-${v.id}`} title="Deactivate (keeps history)"><Trash2 size={14} /></button>
                      )}
                      {isCommittee && v.status === "inactive" && (
                        <button onClick={() => restore(v)} className="text-green-600 hover:text-green-700 p-2" data-testid={`vendor-restore-${v.id}`} title="Restore"><RotateCcw size={14} /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
        {filtered.length === 0 && (
          <div className="text-stone-500 italic py-10 text-center">
            {filter ? `No vendors match "${filter}".` : (showInactive ? "No inactive vendors." : "No vendors yet — start by adding your usual plumber.")}
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------- Review summary + dialog ----------- */
function ReviewSummary({ vendor }) {
  const [open, setOpen] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    if (loaded) return;
    try {
      const r = await api.get(`/vendors/${vendor.id}/ratings`);
      setRatings(r.data); setLoaded(true);
    } catch (err) { console.error("vendor ratings load failed", err); }
  }
  function openDialog() { load(); setOpen(true); }

  if (!vendor.rating_count) {
    return <div className="text-xs text-stone-400 mt-1">No ratings yet</div>;
  }
  return (
    <>
      <button onClick={openDialog}
        data-testid={`vendor-reviews-btn-${vendor.id}`}
        className="inline-flex items-center gap-2 mt-1 text-amber-600 hover:underline">
        <span className="inline-flex items-center gap-0.5 text-amber-500">
          {[1,2,3,4,5].map((s) => (
            <Star key={s} size={12} fill={s <= Math.round(vendor.avg_rating) ? "currentColor" : "none"}
              className={s <= Math.round(vendor.avg_rating) ? "" : "text-stone-300"} />
          ))}
        </span>
        <span className="text-xs font-semibold" data-testid={`vendor-avg-${vendor.id}`}>{vendor.avg_rating.toFixed(1)}</span>
        <span className="text-xs text-stone-500">·</span>
        <span className="text-xs text-stone-500" data-testid={`vendor-reviewcount-${vendor.id}`}>{vendor.rating_count} review{vendor.rating_count !== 1 ? "s" : ""}</span>
        {vendor.comment_count > 0 && (
          <>
            <span className="text-xs text-stone-500">·</span>
            <span className="text-xs text-stone-500 inline-flex items-center gap-1" data-testid={`vendor-commentcount-${vendor.id}`}>
              <MessageSquare size={11} /> {vendor.comment_count} comment{vendor.comment_count !== 1 ? "s" : ""}
            </span>
          </>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto" data-testid={`vendor-reviews-${vendor.id}`}>
          <DialogHeader>
            <DialogTitle className="font-serif-display">Reviews for {vendor.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {ratings.length === 0 && <div className="text-stone-500 italic">No reviews loaded.</div>}
            {ratings.map((r, idx) => (
              <div key={r.id || `${r.user_name}-${r.created_at}`} className="border-b border-stone-100 pb-3 last:border-b-0" data-testid={`vendor-review-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center text-amber-500">
                    {[1,2,3,4,5].map((s) => <Star key={s} size={12} fill={s <= r.rating ? "currentColor" : "none"} className={s <= r.rating ? "" : "text-stone-300"} />)}
                  </span>
                  <span className="text-sm font-medium">{r.user_name}</span>
                  <span className="text-xs text-stone-400">· {timeAgo(r.created_at)}</span>
                </div>
                {r.comment && <div className="text-sm text-stone-700 mt-1">{r.comment}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ----------- Rate dialog ----------- */
function RateVendorDialog({ vendor, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(vendor.my_rating || 0);
  const [comment, setComment] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (rating < 1) return toast.error("Pick 1–5 stars");
    setBusy(true);
    try {
      await api.post(`/vendors/${vendor.id}/rate`, { rating, comment, anonymous });
      toast.success("Rating saved"); setOpen(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`vendor-rate-btn-${vendor.id}`}
          className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${
            vendor.my_rating ? "border-amber-300 text-amber-700 bg-amber-50" : "border-stone-300 text-stone-600 hover:border-amber-400 hover:text-amber-700"
          }`}>
          <Star size={12} fill={vendor.my_rating ? "currentColor" : "none"} />
          {vendor.my_rating ? `${vendor.my_rating}/5` : "Rate"}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">Rate {vendor.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid={`vendor-rate-form-${vendor.id}`}>
          <div>
            <Label>Your rating</Label>
            <div className="flex gap-1 mt-1.5">
              {[1, 2, 3, 4, 5].map((r) => (
                <button type="button" key={r} onClick={() => setRating(r)}
                  data-testid={`vendor-rate-${vendor.id}-${r}`}
                  className={r <= rating ? "text-amber-500" : "text-stone-300"}>
                  <Star size={26} fill="currentColor" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Comment (optional)</Label>
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
              data-testid={`vendor-rate-comment-${vendor.id}`}
              placeholder="Reliable. Slightly pricey on weekends." className="mt-1.5" />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)}
              data-testid={`vendor-rate-anon-${vendor.id}`} className="rounded" />
            Post as anonymous (committee can still see who rated)
          </label>
          <Button type="submit" disabled={busy} data-testid={`vendor-rate-save-${vendor.id}`}
            className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Saving…" : "Save rating"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ----------- Add vendor dialog ----------- */
function AddVendorDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(""); const [type, setType] = useState("");
  const [phone, setPhone] = useState(""); const [notes, setNotes] = useState("");
  const [rating, setRating] = useState(0); const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/vendors", { name, service_type: type, phone, notes, rating });
      toast.success(`${name} added`);
      setName(""); setType(""); setPhone(""); setNotes(""); setRating(0); setOpen(false);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-vendor-btn" className="rounded-full bg-[#C05640] hover:bg-[#A54734]">
          <Plus size={16} className="mr-1.5" /> Add vendor
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif-display">Add a vendor</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="vendor-form">
          <div><Label>Name</Label><Input value={name} required onChange={(e) => setName(e.target.value)} data-testid="v-name" className="h-11 mt-1.5" placeholder="Ravi Plumbing" /></div>
          <div><Label>Service type</Label><Input value={type} required onChange={(e) => setType(e.target.value)} data-testid="v-type" className="h-11 mt-1.5" placeholder="Plumber / Electrician / Carpenter…" /></div>
          <div><Label>Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="v-phone" className="h-11 mt-1.5" placeholder="+91-…" /></div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="v-notes" className="mt-1.5" placeholder="Reliable. Charges ₹500/visit." /></div>
          <div>
            <Label>Your rating</Label>
            <div className="flex gap-1 mt-1.5">
              {[1, 2, 3, 4, 5].map((r) => (
                <button type="button" key={r} onClick={() => setRating(r)} className={r <= rating ? "text-amber-500" : "text-stone-300"} data-testid={`v-rate-${r}`}>
                  <Star size={22} fill="currentColor" />
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="v-save"
            className="w-full h-11 rounded-full bg-[#C05640] hover:bg-[#A54734]">{busy ? "Saving…" : "Add vendor"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
